import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppState, Platform, Pressable, View, StyleSheet, ActivityIndicator } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { toolRunsApi } from '@/lib/api';
import { useToolStore } from '@/store';
import useAppActive from '@/hooks/useAppActive';
import PipelineProgress from '@/components/Tools/AdvancedComponents/PipelineProgress';

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_DURATION_MS = 10 * 60 * 1000; // 10 min safety bail-out
const PENDING_STATES = new Set(['pending', 'running']);
const IS_TOUCH_NATIVE = Platform.OS === 'ios' || Platform.OS === 'android';

/**
 * Polls the backend for an in-flight ToolRun and renders a progress strip.
 *
 * Used by image / video / music tools where the underlying InferenceJob may
 * complete asynchronously after the initial /run response returns.
 *
 * Props:
 *  - run: the current ToolRun object (must have `id` and `status`)
 *  - onUpdate(updatedRun): called whenever the polled run object changes
 */
function ToolProgressIndicator({ run, onUpdate }) {
  const { colors } = useTheme();
  const upsertRunInStore = useUpsertRun();
  // Gated on app/visibility state: a hidden/background tab should not keep
  // polling `/tool-runs/:id/poll` every 3s for up to 10 minutes. The main
  // poll effect below lists this as a dependency, so transitioning to
  // inactive cancels the in-flight setTimeout and re-entering the effect on
  // resume kicks off a fresh poll immediately.
  const isAppActive = useAppActive();
  const [polledStatus, setPolledStatus] = useState(run?.status || 'pending');
  const [pollError, setPollError]       = useState(null);
  // True while a poll request is actually in flight. Kept separate from
  // `pollError` so the UI can render "Reconnecting…" while a retry attempt
  // is mid-flight even though the previous attempt's error is still in
  // state — otherwise the screen looks frozen on the error message during
  // a 6-second back-off and users assume the app is dead.
  const [isReconnecting, setIsReconnecting] = useState(false);
  // 0-100 from the most-recently-polled InferenceJob, or null when the
  // provider doesn't report one (e.g. quick text-only models — we just show
  // the spinner + duration in that case).
  const [progress, setProgress]         = useState(null);
  // Wall-clock duration in seconds, ticked every 1s by an interval below.
  // Surfaced so the user has *some* sense of how long the request has been
  // running — staring at a spinner that never moves feels broken.
  const [elapsedSec, setElapsedSec]     = useState(0);
  // Bump this counter to force the polling effect to tear down the current
  // tick loop and start a fresh one. Drives manual retry (button) and
  // automatic retry-on-foreground (AppState listener) in a single place.
  const [pollAttempt, setPollAttempt] = useState(0);
  const startedAtRef = useRef(Date.now());

  const isPending = PENDING_STATES.has(polledStatus);

  useEffect(() => {
    setPolledStatus(run?.status || 'pending');
    startedAtRef.current = Date.now();
    setPollError(null);
    setIsReconnecting(false);
    setProgress(null);
    setElapsedSec(0);
    setPollAttempt(0);
  }, [run?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lightweight 1Hz wall-clock tick. Independent of the network poll loop so
  // the duration counter keeps moving smoothly even when the server-side
  // poll is throttled (the poll endpoint is rate-limited to 2-3s).
  useEffect(() => {
    if (!isPending) return;
    const t = setInterval(() => {
      setElapsedSec(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 1000);
    return () => clearInterval(t);
  }, [isPending]);

  // Recovery primitive — wired to both the manual "Retry" button and the
  // automatic foreground recovery effect below. Resets the safety clock so
  // a long device sleep doesn't immediately re-trip the 10-minute bail-out
  // when the JS bridge resumes.
  const triggerRetry = useCallback(() => {
    setPollError(null);
    setIsReconnecting(true);
    startedAtRef.current = Date.now();
    setPollAttempt(v => v + 1);
  }, []);

  // Auto-recover on app foreground. iOS suspends the JS bridge while the
  // device is locked; when we come back, any in-flight setTimeout has either
  // already failed (network was unreachable while transitioning out of the
  // lock screen) OR is about to. Either way, the safest move is to assume
  // the in-flight tick is stale and immediately kick off a fresh one.
  //
  // Native-only: on web, `useAppActive` already gates the main poll effect
  // below (tearing it down on `hidden` and re-entering on `visible`), so
  // there's nothing extra to do here. Keeping this listener native-only
  // preserves the `startedAtRef` reset on lock-screen resume, which the web
  // path doesn't need (tab-hide isn't counted as active polling time anyway).
  useEffect(() => {
    if (!IS_TOUCH_NATIVE) return;
    if (!isPending) return;
    const sub = AppState.addEventListener('change', (next) => {
      if (next !== 'active') return;
      // Only recover when there's an active error. If the loop was running
      // happily before lock, the natural tick will resume on its own.
      if (pollError) {
        triggerRetry();
      } else {
        // Even with no error, force one immediate poll to shorten the gap
        // between "phone awake" and "result on screen" — the server may
        // have finished while we were asleep.
        setPollAttempt(v => v + 1);
      }
    });
    return () => sub?.remove?.();
  }, [isPending, pollError, triggerRetry]);

  useEffect(() => {
    // Pause the poll loop entirely when the tab is hidden / app is in the
    // background. Without this, a pending tool run in a backgrounded web tab
    // would keep hitting `/tool-runs/:id/poll` every 3s for up to 10 minutes
    // (the MAX_POLL_DURATION_MS bail-out). On becoming visible/active again
    // this effect tears down + re-runs, kicking off a fresh tick immediately.
    if (!run?.id || !PENDING_STATES.has(run.status) || !isAppActive) return;

    let cancelled = false;
    let timer = null;

    const tick = async () => {
      if (cancelled) return;
      // Safety bail-out so we don't poll forever on a stuck job. The clock
      // is reset by `triggerRetry()` when the user manually retries or the
      // app comes back from background, so a long device sleep doesn't
      // immediately bail-out — the cap measures *active foreground time*.
      if (Date.now() - startedAtRef.current > MAX_POLL_DURATION_MS) {
        setPollError('Run is taking longer than expected.');
        setIsReconnecting(false);
        return;
      }
      setIsReconnecting(true);
      try {
        // Use the server-side poll endpoint (not getById) so any pending
        // InferenceJobs actually get advanced from the provider — otherwise
        // the run sits at "running" forever in environments where the
        // background polling worker isn't running (e.g. local dev).
        const data = await toolRunsApi.poll(run.id);
        const fresh = data?.toolRun;
        if (cancelled || !fresh) return;
        // Successful poll: clear any prior error so the UI relaxes back to
        // the normal "Running…" state. Keeps users from staring at a stale
        // "Unable to connect" message that's already been resolved.
        setPollError(null);
        setIsReconnecting(false);
        setPolledStatus(fresh.status);
        upsertRunInStore(fresh);
        onUpdate?.(fresh);

        // Pull provider-reported job progress (0-100) out of the response.
        // We pick the most-advanced non-terminal job — multi-step pipelines
        // are rare today, but if/when they show up the user should see the
        // job that's actively running rather than e.g. a still-queued one.
        const jobs = Array.isArray(data?.jobs) ? data.jobs : [];
        if (jobs.length) {
          const active = jobs.find(j => !['completed', 'failed', 'cancelled'].includes(j.status)) || jobs[jobs.length - 1];
          if (active && typeof active.progress === 'number' && !Number.isNaN(active.progress)) {
            setProgress(Math.min(100, Math.max(0, active.progress)));
          }
        }

        if (PENDING_STATES.has(fresh.status)) {
          timer = setTimeout(tick, POLL_INTERVAL_MS);
        }
      } catch (e) {
        if (cancelled) return;
        setPollError(e?.message || 'Polling failed');
        // We're no longer in flight, but we will be again when the timer
        // fires. Marking false here keeps the "Reconnecting…" flicker honest.
        setIsReconnecting(false);
        timer = setTimeout(tick, POLL_INTERVAL_MS * 2);
      }
    };

    timer = setTimeout(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [run?.id, run?.status, onUpdate, upsertRunInStore, pollAttempt, isAppActive]);

  const styles = useMemo(() => StyleSheet.create({
    container: {
      gap: 10,
      paddingVertical: 10,
      paddingHorizontal: 12,
      borderRadius: 8,
      backgroundColor: colors.solid_color_tertiary,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
    },
    statusRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    // Small right-aligned cluster: percentage + elapsed timer. Pinned to
    // the end of the status row via marginLeft:'auto' so it floats against
    // the right edge regardless of the label length.
    statusRight: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      marginLeft: 'auto',
    },
    progressBarTrack: {
      width: '100%',
      height: 6,
      borderRadius: 999,
      backgroundColor: colors.solid_color_block,
      overflow: 'hidden',
    },
    progressBarFill: {
      height: '100%',
      borderRadius: 999,
      backgroundColor: colors.text_color_secondary,
    },
    // Manual retry pill rendered inline with the status row whenever we're
    // sitting on a `pollError`. Keeps the recovery action visually anchored
    // to the failure message instead of a separate "have you tried turning
    // it off and on again?" banner. Hidden while a retry is mid-flight so
    // a fast double-tap can't fire two simultaneous polls.
    retryButton: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingVertical: 4,
      paddingHorizontal: 9,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: colors.border_color_primary,
      backgroundColor: colors.solid_color_block,
    },
    retryButtonDisabled: {
      opacity: 0.55,
    },
  }), [colors]);

  if (!run || !isPending) return null;

  const baseLabel = polledStatus === 'pending' ? 'Queued' : 'Running';
  // Status text precedence: while a retry attempt is in flight, show
  // "Reconnecting…" so users can see the app is actively trying to recover.
  // While a poll error is sitting (between retries) show the error verbatim
  // so users get the underlying reason. Otherwise fall back to the polled
  // pipeline state ("Queued…" / "Running…").
  const label = isReconnecting
    ? 'Reconnecting…'
    : pollError
      ? pollError
      : `${baseLabel}…`;
  const pct = progress != null ? Math.round(progress) : null;
  const showRetry = !!pollError && !isReconnecting;

  return (
    <View style={styles.container}>
      <View style={styles.statusRow}>
        <ActivityIndicator size='small' color={colors.text_color_secondary} />
        <Text size='s' color='secondary' weight='medium' style={{ flex: 1, minWidth: 0 }} numberOfLines={2}>
          {label}
        </Text>
        <View style={styles.statusRight}>
          {showRetry && (
            <Pressable
              onPress={triggerRetry}
              disabled={isReconnecting}
              hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
              accessibilityRole='button'
              accessibilityLabel='Retry'
              style={[styles.retryButton, isReconnecting && styles.retryButtonDisabled]}
            >
              <Icon name='arrow-rotate-right' size={10} color={colors.text_color_primary} />
              <Text size='xs' weight='bold' color='primary'>Retry</Text>
            </Pressable>
          )}
          {pct != null && (
            <Text size='xs' color='secondary' weight='medium'>{pct}%</Text>
          )}
          <Text size='xs' color='tertiary'>{formatDuration(elapsedSec)}</Text>
        </View>
      </View>

      {/* Real progress bar when the provider actually reports a percentage.
          For bursty providers that don't (e.g. text models that return in a
          single shot) we fall back to the pipeline strip below. */}
      {pct != null && (
        <View style={styles.progressBarTrack}>
          <View style={[styles.progressBarFill, { width: `${pct}%` }]} />
        </View>
      )}

      <PipelineProgress
        status={pollError ? 'attention needed' : baseLabel.toLowerCase()}
        activeIndex={polledStatus === 'pending' ? 0 : 1}
        stages={['Queued', 'Running', 'Finalizing', 'Complete']}
      />
    </View>
  );
}

// Compact "1:23" / "0:42" formatter. Stays in m:ss until 1h, then h:mm:ss.
function formatDuration(totalSec) {
  const s = Math.max(0, Math.floor(totalSec || 0));
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const pad = n => String(n).padStart(2, '0');
  if (hh > 0) return `${hh}:${pad(mm)}:${pad(ss)}`;
  return `${mm}:${pad(ss)}`;
}

// Tiny helper that lets the indicator hydrate the global tool-run cache
// without importing the entire store API into the polling loop.
function useUpsertRun() {
  return useMemo(() => (run) => {
    if (!run?.id) return;
    const state = useToolStore.getState();
    const slug = run.toolSlug;
    const set = useToolStore.setState;
    set({
      runsByToolSlug: {
        ...state.runsByToolSlug,
        [slug]: upsertInList(state.runsByToolSlug[slug] || [], run),
        __all__: upsertInList(state.runsByToolSlug.__all__ || [], run),
      },
    });
  }, []);
}

function upsertInList(list, run) {
  const idx = list.findIndex(r => r.id === run.id);
  if (idx === -1) return [run, ...list];
  const next = list.slice();
  next[idx] = { ...next[idx], ...run };
  return next;
}

export default React.memo(ToolProgressIndicator);
